import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PieChart from "examples/Charts/PieChart";
import ChartHeader from "examples/Charts/PieChart/ChartHeader";
import Icon from "@mui/material/Icon";
import MetricCardsContainer from "layouts/insight-hub/components/MetricCardsContainer";
import { Typography } from "@mui/material";

import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

import EntitiesPopup from "layouts/components/EntitiesPopup";
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled";

import ChartAssistant from "components/ChartAssistant"; // ✅ Import ChartAssistant

// ... (existing imports)

// -----------------------------------------------------------
// 🚀 SENTIMENT COLORS (Teal, Yellow, Red)
// -----------------------------------------------------------
const SENTIMENT_COLORS = {
    Positive: "rgba(75, 192, 192, 0.8)", // Teal (Score > 60)
    Neutral: "rgba(255, 205, 86, 0.8)",  // Yellow (40 <= Score <= 60)
    Negative: "rgba(255, 99, 132, 0.8)", // Red (Score < 40)
};

function CommentSentimentChart({
    title,
    datasets,
    previousdatasets = [],
    isLoading = false,
    isEntitled = true,
    featureName = "",
    planRequired = "",
    entitlements // ✅ Added
}) {

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupFilter, setPopupFilter] = useState({ profileId: null });

    // 1. Calculate Pie Chart Data per Profile
    const chartDataByProfile = useMemo(() => {
        if (!datasets || datasets.length === 0) {
            return [];
        }

        return datasets.map((profile) => {
            if (!profile) return null;

            const counts = { Positive: 0, Neutral: 0, Negative: 0 };
            const contentField = profile.platform === "YouTube" ? "videos" : "posts";

            const contentArray = (profile.data || []).flatMap((entry) => entry[contentField] || []);

            contentArray.forEach((content) => {
                const comments = content.comments || [];
                comments.forEach((c) => {
                    // Ensure zoneScore exists and is a number
                    const score = typeof c.sentiment?.zoneScore === 'number'
                        ? c.sentiment.zoneScore
                        : null; // or default to null/skip

                    if (score !== null) {
                        if (score > 60) {
                            counts.Positive++;
                        } else if (score >= 40) {
                            counts.Neutral++;
                        } else {
                            counts.Negative++;
                        }
                    }
                });
            });

            const labels = ["Positive", "Neutral", "Negative"];
            const data = [counts.Positive, counts.Neutral, counts.Negative];
            const backgroundColors = [
                SENTIMENT_COLORS.Positive,
                SENTIMENT_COLORS.Neutral,
                SENTIMENT_COLORS.Negative
            ];

            // Only return if there is at least some data
            const total = counts.Positive + counts.Neutral + counts.Negative;
            if (total === 0) {
                // Return structure even if empty so we can show "No data" state properly handled by renderer?
                // Or just return keys with empty data.
            }

            return {
                profileName: profile.name || profile.label || "Unknown",
                platform: profile.platform,
                profileId: profile.profileId,
                chart: {
                    labels,
                    datasets: {
                        label: "Sentiment Distribution",
                        data,
                        backgroundColors,
                    },
                },
                totalComments: total,
            };
        }).filter(Boolean);
    }, [datasets]);

    // 2. Calculate Top Metrics (Total Comments)
    const commentMetrics = useMemo(() => {
        if (!datasets) return [];

        return datasets.map((profile) => {
            if (!profile) return null;

            const contentField = profile.platform === "YouTube" ? "videos" : "posts";

            const currentCount = (profile.data || []).reduce((acc, entry) => {
                const items = entry[contentField] || [];
                const comments = items.reduce((sum, item) => sum + (item.comments?.length || 0), 0);
                return acc + comments;
            }, 0);

            const previousProfile = previousdatasets?.find((p) => p.profileId === profile.profileId);
            const previousCount = previousProfile
                ? (previousProfile.data || []).reduce((acc, entry) => {
                    const items = entry[contentField] || [];
                    const comments = items.reduce((sum, item) => sum + (item.comments?.length || 0), 0);
                    return acc + comments;
                }, 0)
                : 0;

            return {
                profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
                name: profile.name || profile.label || "Unknown",
                platform: profile.platform,
                metricName: "Total Comments",
                metricValue: currentCount.toLocaleString(),
                previousMetricValue: previousCount.toLocaleString(),
            };
        }).filter(Boolean);
    }, [datasets, previousdatasets]);

    // ⬇️=========================================
    // ⬇️ ADDED: Synthetic Chart Data for AI
    // ⬇️=========================================
    const aiChartData = useMemo(() => {
        if (!chartDataByProfile || chartDataByProfile.length === 0) return null;

        // Labels are fixed: Positive, Neutral, Negative
        const labels = ["Positive", "Neutral", "Negative"];

        // Datasets: One per profile
        const datasets = chartDataByProfile.map(p => {
            // p.chart.data is [Positive, Neutral, Negative]
            return {
                label: `${p.profileName} (${p.platform})`,
                data: p.chart.datasets.data // Already in correct order [Pos, Neut, Neg]
            };
        });

        return { labels, datasets };
    }, [chartDataByProfile]);

    // ⬇️=========================================
    // ⬇️ ADDED: Text Context for AI
    // ⬇️=========================================
    const aiContext = useMemo(() => {
        let text = `Chart Description: Comment Sentiment Analysis (Positive, Neutral, Negative).\n\nInsights:`;
        if (chartDataByProfile) {
            chartDataByProfile.forEach(p => {
                const [pos, neu, neg] = p.chart.datasets.data;
                const total = pos + neu + neg;
                const posPct = total ? ((pos / total) * 100).toFixed(0) : 0;
                const negPct = total ? ((neg / total) * 100).toFixed(0) : 0;

                text += `\n\nProfile: ${p.profileName} (${p.platform})`;
                text += `\n- Positive: ${pos} (${posPct}%)`;
                text += `\n- Neutral: ${neu}`;
                text += `\n- Negative: ${neg} (${negPct}%)`;
            });
        }
        return text;
    }, [chartDataByProfile]);


    // Click handlers
    const handleSliceClick = (label, profileId) => {
        // We filter by profile ID AND sentiment (label)
        setPopupFilter({ profileId, sentiment: label });
        setIsPopupOpen(true);
    };

    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setPopupFilter({ profileId: null, sentiment: null });
    };

    if (isLoading) {
        return (
            <Card>
                <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
                    <CircularProgress size={32} thickness={4} color="accent" />
                </MDBox>
            </Card>
        );
    }

    if (!datasets) {
        return (
            <Card>
                <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">
                        No sentiment data available.
                    </MDTypography>
                </MDBox>
            </Card>
        );
    }

    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase()) {
            case "facebook":
                return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            case "youtube":
                return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            case "instagram":
                return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            case "twitter":
                return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            default:
                return null;
        }
    };

    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                This chart breaks down the comments on your content into three sentiment categories:
                Positive, Neutral, and Negative.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                <span style={{ color: SENTIMENT_COLORS.Positive }}>●</span> <strong>Positive (Green)</strong>: Score &gt; 60<br />
                <span style={{ color: SENTIMENT_COLORS.Neutral }}>●</span> <strong>Neutral (Yellow)</strong>: Score 40-60<br />
                <span style={{ color: SENTIMENT_COLORS.Negative }}>●</span> <strong>Negative (Red)</strong>: Score &lt; 40
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Quickly assess the overall mood of the conversation. <br />
                - Identify profiles receiving critical feedback (Negative). <br />
                - Click a slice to filter and read comments in that category.
            </Typography>
        </div>
    );

    return (
        <>
            <Card style={{ position: "relative", display: "flex", flexDirection: "column" }}>
                {/* 🚫 Entitlement Overlay */}
                {!isEntitled && (
                    <ChartNotEntitled
                        featureName={featureName}
                        planRequired={planRequired}
                    />
                )}

                <ChartHeader
                    title={title}
                    description={tooltipContent}
                    profilePictures={commentMetrics.map(m => m.profilePic)}
                    is_derived={true}
                >
                    {/* ✅ AI Assistant */}
                    <ChartAssistant
                        title={title}
                        context={aiContext}
                        chartData={aiChartData}
                        rawData={datasets}
                        sx={{
                            position: "static",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                            ml: 2
                        }}
                        entitlements={entitlements} // ✅ Pass entitlements
                    />
                </ChartHeader>

                <MetricCardsContainer metrics={commentMetrics} />
                <MDBox mt={1} />

                <MDBox p={3}>
                    <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
                        {chartDataByProfile.map(({ profileName, platform, chart, profileId, totalComments }, idx) => (
                            <React.Fragment key={idx}>
                                {totalComments > 0 ? (
                                    <MDBox
                                        textAlign="center"
                                        width={{ xs: "100%", md: "45%", lg: "30%" }}
                                    >
                                        <MDTypography
                                            variant="subtitle2"
                                            color="text"
                                            mb={1}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            gap={0.5}
                                        >
                                            {getPlatformIcon(platform)}{profileName}
                                        </MDTypography>
                                        <PieChart
                                            chart={chart}
                                            height="15rem"
                                            icon={{ component: "", color: "" }}
                                            title=""
                                            description=""
                                            onSliceClick={(label) => handleSliceClick(label, profileId)}
                                        />
                                        <MDBox display="flex" flexWrap="wrap" justifyContent="center" mt={1} px={3}>
                                            {chart.labels.map((label, index) => (
                                                <MDBox
                                                    key={label}
                                                    display="flex"
                                                    alignItems="center"
                                                    my={0.5}
                                                    mr={3}
                                                    minWidth="10%"
                                                >
                                                    <Icon sx={{ color: chart.datasets.backgroundColors[index], mr: 1, fontSize: "1rem" }}>
                                                        fiber_manual_record
                                                    </Icon>
                                                    <MDTypography variant="button" color="text">
                                                        {label}: {chart.datasets.data[index]}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                ) : (
                                    <MDBox textAlign="center" width={{ xs: "100%", md: "45%", lg: "30%" }}>
                                        <MDTypography
                                            variant="subtitle2"
                                            color="text"
                                            mb={1}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            gap={0.5}
                                        >
                                            {getPlatformIcon(platform)}{profileName}
                                        </MDTypography>
                                        <MDTypography variant="subtitle1" color="text" align="center">
                                            No comments found in the selected date range.
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </React.Fragment>
                        ))}
                    </MDBox>
                </MDBox>

                {/* ✅ Disclaimer Footer */}
                <MDBox p={1} display="flex" justifyContent="flex-end" sx={{ mt: "auto" }}>
                    <MDTypography
                        variant="caption"
                        color="text"
                        sx={{
                            fontStyle: "italic",
                            fontSize: "0.7rem",
                            opacity: 0.7,
                            mr: 1
                        }}
                    >
                        *Metric calculated by Comparedly
                    </MDTypography>
                </MDBox>
            </Card>

            {isPopupOpen && (
                <EntitiesPopup
                    isModalOpen={isPopupOpen}
                    onClose={handlePopupClose}
                    datasets={datasets}
                    filterProfileId={popupFilter.profileId}
                    filterSentiment={popupFilter.sentiment} // ✅ Pass sentiment filter
                />
            )}
        </>
    );
}

CommentSentimentChart.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    datasets: PropTypes.array.isRequired,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
    entitlements: PropTypes.object,
};

export default CommentSentimentChart;


